import { del, delMany, get, keys, set } from 'idb-keyval';
import type { Attachment } from '@/types/db';
import { workerFetch } from './api';

/**
 * Files live in a private Neon Object Storage bucket. The Worker checks `storage_access()` in
 * Postgres as the signed-in user and returns short-lived presigned URLs; browsers never hold
 * bucket credentials.
 *
 * Object keys: `{orgId}/{folder}/{file}` for organization files, `library/{folder}/{file}` for
 * the platform library.
 */
const PREFIX = 'attachment:';

async function downloadUrl(path: string): Promise<string> {
  const { url } = await workerFetch<{ url: string }>('/storage/download-url', { method: 'POST', body: JSON.stringify({ path }) });
  return url;
}

export async function deleteStoredObject(path: string): Promise<void> {
  await workerFetch('/storage/delete', { method: 'POST', body: JSON.stringify({ path }) });
}

/** Opens a stored file in a new tab (short-lived signed URL). */
export async function openStoredFile(path: string) {
  // Open the window synchronously within the click so mobile popup blockers allow it.
  const win = window.open('', '_blank');
  try {
    const url = await downloadUrl(path);
    if (win) win.location.href = url;
    else window.location.href = url;
  } catch (err) {
    win?.close();
    throw err;
  }
}

/** Opens an attachment from the offline cache when available, otherwise from storage. */
export async function openAttachment(attachment: Attachment) {
  const cached = await get<Blob>(PREFIX + attachment.path);
  if (!cached) return openStoredFile(attachment.path);
  const url = URL.createObjectURL(cached);
  window.open(url, '_blank');
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

export async function saveAttachmentOffline(attachment: Attachment) {
  const res = await fetch(await downloadUrl(attachment.path));
  if (!res.ok) throw new Error(`Download failed (${res.status})`);
  await set(PREFIX + attachment.path, await res.blob());
}

export async function removeOfflineAttachment(path: string) {
  await del(PREFIX + path);
}

export async function savedAttachmentPaths(): Promise<Set<string>> {
  const all = await keys();
  return new Set(all.map(String).filter((k) => k.startsWith(PREFIX)).map((k) => k.slice(PREFIX.length)));
}

export async function clearOfflineAttachments() {
  const all = await keys();
  await delMany(all.filter((k) => String(k).startsWith(PREFIX)));
}

export const MAX_UPLOAD_BYTES = 50 * 1024 * 1024;

export async function uploadAttachment(orgId: string | null, folder: string, file: File): Promise<Attachment> {
  if (file.size > MAX_UPLOAD_BYTES) throw new Error('Files must be 50 MB or smaller.');
  const safeName = file.name.replace(/[^a-zA-Z0-9._-]+/g, '-').replace(/^[.-]+/, '').slice(-100) || 'file';
  const path = `${orgId ?? 'library'}/${folder}/${Date.now()}-${safeName}`;
  const contentType = file.type || 'application/octet-stream';
  const { url } = await workerFetch<{ url: string }>('/storage/upload-url', {
    method: 'POST',
    body: JSON.stringify({ path, contentType, size: file.size }),
  });
  const res = await fetch(url, { method: 'PUT', body: file, headers: { 'Content-Type': contentType } });
  if (!res.ok) throw new Error(`Upload failed (${res.status})`);
  return { name: file.name, path, size: file.size, content_type: contentType };
}

export function formatBytes(bytes: number | null | undefined) {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}
